import { Box, TableCell, TableRow } from "@mui/material";
import { DotLegalAlert, DotLegalButton, DotLegalDialog, DotLegalHeader, DotLegalSelect, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import DotLegalTableWithControlsLocalized from "@common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import LinkedControlsSection from "../../riskControls/LinkedControlsSection";
import { RiskOperationalItem } from "../RiskOperationalOverview.types";
import MitigationPlanFormFields from "../../riskAssessmentsOverview/treatmentDialogs/MitigationPlanFormFields";
import { useBulkMitigateDialog } from "./BulkMitigateDialog.hooks";
import { useBulkMitigateDialogStyles } from "./BulkMitigateDialog.styles";
import { BulkMitigateEntityRow } from "./BulkMitigateDialog.types";

export interface IBulkMitigateDialog {
    riskAreaId: string;
    entities: Array<RiskOperationalItem>;
    onRemoveEntity: (entityKey: string) => void;
    onSaved: () => void;
    onClose: () => void;
}

function BulkMitigateDialog(props: IBulkMitigateDialog) {
    const styles = useBulkMitigateDialogStyles();
    const { translateString } = useTranslation();

    const {
        planForm,
        entities,
        entityRows,
        removeEntity,
        isLoading,
        isValid,
        inProgress,
        onSave,
        scenarioOptions,
        selectedScenarioId,
        setSelectedScenarioId,
        scenarioError,
        consequenceOptions,
        probabilityOptions,
    } = useBulkMitigateDialog(props);

    const entityHeaders: Array<ITableHeader<BulkMitigateEntityRow>> = [
        { property: "typeLabel", text: translateString("type") },
        { property: "name", text: translateString("entity") },
        { property: null, text: "", align: "right", notSortable: true },
    ];

    return (
        <DotLegalDialog
            open
            size="md"
            isLoading={isLoading}
            inProgress={inProgress}
            header={translateString("bulkMitigation")}
            buttonOkText={translateString("save")}
            onOkClick={onSave}
            okButtonDisabled={!isValid}
            secondaryButtonText={translateString("cancel")}
            secondaryButtonStyle="secondary"
            onSecondaryButtonClick={props.onClose}
            onDialogClose={props.onClose}
        >
            <Box sx={styles.content}>
                <Box sx={styles.subHeader}>
                    <DotLegalHeader center marginTop={-1} marginBottom={0} fontWeight={400} headerStyle="small">
                        {translateString("bulkMitigationDescription")}
                    </DotLegalHeader>
                </Box>

                <DotLegalAlert severity="info" warningText={translateString("bulkMitigateScenarioInfo")} marginTop={0} marginBottom={0} />

                <DotLegalSection>
                    <Box sx={styles.entityTable}>
                        <DotLegalTableWithControlsLocalized
                            extraControls={
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <DotLegalHeader headerStyle="small" color="primary" marginBottom={0}>
                                        {translateString("bulkMitigateSelectedEntities", { count: entities.length })}
                                    </DotLegalHeader>
                                </Box>
                            }
                            headers={entityHeaders}
                            data={entityRows}
                            isLoading={isLoading}
                            defaultOrderBy="name"
                            defaultOrder="asc"
                            columnsExcludedFromFilter={["key", "type"]}
                            getUserSpecificPageLength={() => 9999}
                            clickableRows={false}
                            emptyText={translateString("noData")}
                            renderRow={(row) => (
                                <TableRow key={row.key}>
                                    <TableCell>{row.typeLabel}</TableCell>
                                    <TableCell>
                                        <EntityTypeIconWithName type={row.type} name={row.name} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <DotLegalButton buttonType="linkButton" size="small" onClick={() => removeEntity(row.key)}>
                                            {translateString("remove")}
                                        </DotLegalButton>
                                    </TableCell>
                                </TableRow>
                            )}
                        />
                    </Box>
                </DotLegalSection>

                <DotLegalSection>
                    <DotLegalHeader headerStyle="small" color="primary" marginBottom={1}>
                        {translateString("chooseScenario")}
                    </DotLegalHeader>
                    <DotLegalSelect
                        label={translateString("riskScenario")}
                        placeholder={translateString("chooseScenario")}
                        options={scenarioOptions}
                        selectedItem={selectedScenarioId}
                        onChange={setSelectedScenarioId}
                        noOptionsLabel={translateString("noOptions")}
                        errorText={scenarioError}
                        noMargin
                    />
                </DotLegalSection>

                <MitigationPlanFormFields fields={planForm} consequenceOptions={consequenceOptions} probabilityOptions={probabilityOptions} />

                {/* A single shared control selection for every plan; unlike the single-scenario dialog it cannot
                    pre-exclude head controls (each entity has its own), so the full selectable set is offered and
                    the backend strips any already on a given entity's head per assessment. */}
                {planForm.showLinkedControls && (
                    <LinkedControlsSection
                        linkedControlIds={planForm.frameworkControlIds}
                        availableControls={planForm.allControls}
                        onChange={planForm.setFrameworkControlIds}
                        heading="controlsAddedByThisPlan"
                    />
                )}
            </Box>
        </DotLegalDialog>
    );
}

export default BulkMitigateDialog;
